import { Activity, IndianRupee, ShoppingBag, Star } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { SectionHeader } from '@/components/charts/SectionHeader';
import { KpiTile } from '@/components/charts/KpiTile';
import { TrendChart } from '@/components/charts/TrendChart';
import { MotionCard } from '@/components/motion/MotionCard';
import { EmptyState } from '@/components/charts/EmptyState';
import { MoversList } from '@/components/charts/MoversList';
import { useDashboard } from '@/hooks/useDashboardContext';
import { useMetric } from '@/hooks/useMetric';
import { useOutletCapabilities } from '@/hooks/useOutlets';
import { pickMovers, useOutletRanking } from '@/hooks/useOutletRanking';
import { fmtISTRange, formatINRCompact, formatNumberCompact } from '@/lib/format';

export function OverviewPage() {
  const { period, outletId } = useDashboard();
  const caps = useOutletCapabilities(outletId);

  const revenue = useMetric({ outletId, source: 'net_sales', period });
  const orders = useMetric({ outletId, source: 'orders', period });
  const gmv = useMetric({ outletId, source: 'aggregator_gmv', period });
  const adSpend = useMetric({ outletId, source: 'aggregator_ads_spend', period });
  const ranking = useOutletRanking({ source: 'net_sales', period });
  const topMovers = ranking.data ? pickMovers(ranking.data.rankings, 5) : [];

  return (
    <div className="space-y-8">
      <SectionHeader
        title="Overview"
        description={`${revenue.current.label} · ${fmtISTRange(revenue.current.start, revenue.current.end)} vs ${fmtISTRange(revenue.previous.start, revenue.previous.end)}`}
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiTile
          label="Net revenue"
          icon={<IndianRupee className="h-3.5 w-3.5" />}
          metric={revenue.data?.metric}
          loading={revenue.isLoading}
          format={formatINRCompact}
          sparkline={revenue.data?.trend.map((p) => p.current)}
          comparisonRange={{ start: revenue.previous.start, end: revenue.previous.end }}
        />
        <KpiTile
          label="Orders"
          icon={<ShoppingBag className="h-3.5 w-3.5" />}
          metric={orders.data?.metric}
          loading={orders.isLoading}
          format={formatNumberCompact}
          sparkline={orders.data?.trend.map((p) => p.current)}
          comparisonRange={{ start: orders.previous.start, end: orders.previous.end }}
          delay={0.05}
        />
        {caps.aggregator && (
          <KpiTile
            label="Aggregator GMV"
            icon={<Star className="h-3.5 w-3.5" />}
            metric={gmv.data?.metric}
            loading={gmv.isLoading}
            format={formatINRCompact}
            sparkline={gmv.data?.trend.map((p) => p.current)}
            comparisonRange={{ start: gmv.previous.start, end: gmv.previous.end }}
            delay={0.1}
          />
        )}
        <KpiTile
          label="Aggregator ad spend"
          icon={<Activity className="h-3.5 w-3.5" />}
          metric={adSpend.data?.metric}
          loading={adSpend.isLoading}
          format={formatINRCompact}
          sparkline={adSpend.data?.trend.map((p) => p.current)}
          comparisonRange={{ start: adSpend.previous.start, end: adSpend.previous.end }}
          delay={0.15}
          invertDelta
        />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2">
          {revenue.data && revenue.data.trend.length > 0 ? (
            <TrendChart
              title="Revenue trend"
              data={revenue.data.trend}
              formatValue={formatINRCompact}
              loading={revenue.isLoading}
            />
          ) : (
            <Card>
              <CardHeader>
                <CardTitle>Revenue trend</CardTitle>
              </CardHeader>
              <CardContent>
                <EmptyState
                  title={
                    revenue.isError
                      ? 'Could not load trend'
                      : revenue.isLoading
                        ? 'Loading…'
                        : 'No data for this period yet'
                  }
                  description={
                    revenue.isError
                      ? 'Check that VITE_SUPABASE_MARKETING_URL is set and sync-sales has run.'
                      : 'Once sync-sales backfills, the trend will appear here.'
                  }
                />
              </CardContent>
            </Card>
          )}
        </div>
        <MotionCard>
          <Card>
            <CardHeader>
              <CardTitle>Top movers (revenue)</CardTitle>
            </CardHeader>
            <CardContent className="px-5 pb-3">
              <MoversList
                rankings={topMovers}
                loading={ranking.isLoading}
                format={formatINRCompact}
                emptyTitle="No movers"
                emptyDescription="All outlets are within their baseline for this period."
              />
            </CardContent>
          </Card>
        </MotionCard>
      </div>
    </div>
  );
}
